import React, { useState, useRef, useMemo } from 'react';
import { alpha, styled } from '@mui/material/styles';

import UIKitMenu from './Menu';
import UIKitIcon from './Icon';

import { radius, colors, ThemeColor } from '../theme';
import { uikitPalette } from '../theme/uikitPalette';

export const DropDownContext = React.createContext<{
  onClose: () => void;
}>({
  onClose: () => {},
});

export interface DropDownStyleProps {
  minWidth?: number;
  width?: number | string;
  minHeight?: number;
  height?: number | string;
  fontsize?: number | string;
  fontweight?: string | number;
}

interface DropDownShellProps {
  text: string;
  label?: string;
  htmlId?: string;
  leftIcon?: React.ReactNode;
  color?: ThemeColor;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  dropDownStyles?: DropDownStyleProps;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode; // contenido del menú
  fullWidth?: boolean;
}

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  width: fullWidth ? '100%' : 'auto',
}));

const Label = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.primary,
  marginBottom: 4,
}));

const Trigger = styled('button', {
  shouldForwardProp: (prop) =>
    !['fullWidth', 'uiWidth', 'uiMinHeight', 'uiHeight', 'error', 'accentColor', 'open'].includes(
      prop as string
    ),
})<{
  fullWidth?: boolean;
  uiWidth?: number | string;
  uiMinHeight?: number;
  uiHeight?: number | string;
  error?: boolean;
  accentColor: string;
  open?: boolean;
}>(({ fullWidth, uiWidth, uiMinHeight, uiHeight, error, accentColor, open }) => {
  const getWidth = () => {
    if (fullWidth) {
      return '100%';
    }
    if (typeof uiWidth !== 'undefined') {
      return `${uiWidth}px`;
    }
    return 'auto';
  };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: getWidth(),
    minHeight: typeof uiMinHeight === 'number' ? uiHeight : 48,
    height: typeof uiHeight !== 'undefined' ? uiHeight : 'auto',
    padding: '0 16px',
    borderRadius: radius.xs,
    border: `1px solid ${error ? colors.error[500] : colors.neutralVariant[500]}`,
    backgroundColor: colors.specialColors.white,
    cursor: 'pointer',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: error ? colors.error[500] : colors.neutral[500],
    },
    ...(open && {
      borderColor: error ? colors.error[500] : accentColor,
      boxShadow: `0 0 0 1px ${alpha(error ? colors.error[500] : accentColor, 0.32)}`,
    }),
    '&:disabled': {
      cursor: 'default',
      borderColor: colors.neutralVariant[100],
      backgroundColor: colors.neutralVariant[50],
      color: colors.neutralVariant[500],
    },
  };
});

const ValueWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  gap: 8,
});

const ValueText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'uiFontSize' && prop !== 'uiFontWeight',
})<{ uiFontSize?: number | string; uiFontWeight?: number | string }>(
  ({ theme, uiFontSize, uiFontWeight }) => ({
    ...theme.typography.body1,
    fontSize: uiFontSize || theme.typography.body1.fontSize,
    fontWeight: (uiFontWeight ||
      theme.typography.body1.fontWeight) as React.CSSProperties['fontWeight'],
    color: colors.neutral[900],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  })
);

const RightIcon = styled('span')({
  marginLeft: 8,
  display: 'flex',
  alignItems: 'center',
});

const HelperText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean }>(({ theme, error }) => ({
  ...theme.typography.caption,
  color: error ? colors.error[500] : colors.neutral[600],
  marginTop: 4,
}));

const UIKitDropDownShell: React.FC<DropDownShellProps> = ({
  text,
  label,
  htmlId = 'uikit-dropdown',
  leftIcon,
  color = 'primary',
  disabled = false,
  error = false,
  helperText,
  dropDownStyles,
  className,
  style,
  children,
  fullWidth = false,
}) => {
  const accentColor = uikitPalette[color].main;

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    // medimos el ancho REAL del trigger en el momento de abrir
    const { current } = triggerRef;
    if (current) {
      setMenuWidth(current.offsetWidth);
    }

    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const dropDownContextValue = useMemo(() => ({ onClose: handleClose }), [handleClose]);

  // construimos los estilos para el menú
  const menuStyles: DropDownStyleProps = {};

  // si el caller pasó width explícito, respetamos eso
  if (dropDownStyles) {
    if (typeof dropDownStyles.minWidth !== 'undefined') {
      menuStyles.minWidth = dropDownStyles.minWidth;
    }
    if (typeof dropDownStyles.width !== 'undefined') {
      menuStyles.width = dropDownStyles.width;
    }
  }

  // si NO pasó width, usamos el ancho del trigger que medimos
  if (typeof menuStyles.width === 'undefined' && typeof menuWidth === 'number') {
    menuStyles.minWidth = menuWidth;
    menuStyles.width = menuWidth;
  }

  return (
    <Root fullWidth={fullWidth} className={className} style={style}>
      {label && <Label>{label}</Label>}

      <Trigger
        type="button"
        id={htmlId}
        ref={triggerRef}
        fullWidth={fullWidth}
        uiWidth={dropDownStyles && dropDownStyles.width}
        uiMinHeight={dropDownStyles && dropDownStyles.minHeight}
        uiHeight={dropDownStyles && dropDownStyles.height}
        error={error}
        accentColor={accentColor}
        open={open}
        disabled={disabled}
        onClick={handleOpen}
      >
        <ValueWrapper>
          {leftIcon}
          <ValueText
            uiFontSize={dropDownStyles && dropDownStyles.fontsize}
            uiFontWeight={dropDownStyles && dropDownStyles.fontweight}
          >
            {text}
          </ValueText>
        </ValueWrapper>

        <RightIcon>
          <UIKitIcon name={open ? 'expand_less' : 'expand_more'} />
        </RightIcon>
      </Trigger>

      <UIKitMenu
        id={`${htmlId}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        menuStyles={menuStyles}
        scrollable
      >
        <DropDownContext.Provider value={dropDownContextValue}>{children}</DropDownContext.Provider>
      </UIKitMenu>

      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </Root>
  );
};

export default UIKitDropDownShell;
